/**
 * «Здоровье данных» — общая часть для сервера и браузера: что считается
 * проблемой, с какими порогами, и как это называется по-человечески.
 * Чтение базы — в lib/data-health-store.ts.
 *
 * Зачем. Сервис и раньше знал, что не доехало, но показывал это только в окне
 * обновления: закрыл — и вчерашнее падение узнать негде. А молчаливые беды
 * («заказы стоят на позапрошлой неделе», «три дня без снимка остатков»,
 * «таблица перестала читаться») не видел никто, и человек считал по неполным
 * данным, не зная об этом.
 *
 * Правило тишины: пока всё в порядке, значок в шапке не появляется. Поэтому
 * пороги подобраны так, чтобы НЕ кричать на нормальную жизнь данных: реклама
 * у Ozon всегда приезжает с задержкой, финансовые операции — тем более, и
 * отставание на день у них не поломка.
 */

import type { DataCoverageSource } from "@/lib/data-coverage";

/** Источники дневных данных календаря здоровья: четыре из «данные до …» плюс снимок остатков. */
export const DATA_HEALTH_SOURCES = ["orders", "ads", "analytics", "finance", "stock"] as const;
export type DataHealthSource = typeof DATA_HEALTH_SOURCES[number];

export const DATA_HEALTH_SOURCE_LABELS: Record<DataHealthSource, string> = {
  orders: "Заказы",
  ads: "Реклама",
  analytics: "Аналитика",
  finance: "Финансы",
  stock: "Остатки по дням",
};

/**
 * Со скольких дней отставания источник считается отставшим. Разные, потому что
 * задержка у источников разная и одинаковый порог давал бы вечную тревогу по
 * рекламе и финансам.
 */
export const DATA_HEALTH_STALE_DAYS: Record<DataCoverageSource, number> = {
  orders: 2,
  analytics: 3,
  ads: 4,
  finance: 7,
};

/** Ночное обновление идёт каждую ночь в 01:00 МСК: больше 36 часов = ночь пропущена. */
export const DATA_HEALTH_REFRESH_LATE_HOURS = 36;

/** Окно, в котором ищем дыры для значка в шапке. */
export const DATA_HEALTH_GAP_WINDOW_DAYS = 30;

/** Окно «ковра дней» на подробной вкладке. */
export const DATA_HEALTH_DETAIL_DAYS = 90;

/**
 * Дыры считаем ТОЛЬКО по снимку остатков: он пишется каждый день по всем
 * товарам, и пропущенный день — всегда пропущенное обновление. У заказов,
 * рекламы и аналитики день без строк может быть честным (не было продаж, не
 * крутили рекламу) — такое в тревогу не превращаем, оно видно на ковре дней.
 */
export const DATA_HEALTH_GAP_SOURCE: DataHealthSource = "stock";

/** Сколько строк источника должно совпасть с товарами, чтобы он не считался сломанным. */
export const DATA_HEALTH_EXTERNAL_MATCH_SHARE = 0.5;

export type DataHealthSeverity = "error" | "warn";

export type DataHealthIssueKind =
  /** Ключи Ozon не пускают — без них не обновится ничего. */
  | "auth"
  /** Последнее обновление упало или прошло с замечаниями. */
  | "refresh_failed"
  /** Обновления давно не было вообще. */
  | "refresh_late"
  /** Источник отстаёт сильнее своего порога. */
  | "stale"
  /** Дни без снимка остатков внутри окна. */
  | "gap"
  /** Своя таблица не читается или почти не совпадает с товарами. */
  | "external";

/** Что предлагаем сделать: кнопка или ссылка рядом с проблемой. */
export type DataHealthAction =
  | { kind: "refresh"; label: string; storeId: string }
  | { kind: "keys"; label: string; storeId: string }
  | { kind: "sources"; label: string }
  | { kind: "details"; label: string };

export type DataHealthIssue = {
  /** Устойчив между запросами: по нему список не «прыгает» при обновлении. */
  id: string;
  kind: DataHealthIssueKind;
  severity: DataHealthSeverity;
  storeId: string | null;
  storeName: string;
  /** Короткая фраза для списка: «Реклама отстаёт на 5 дней». */
  title: string;
  /** Пояснение под ней: что это значит и почему. */
  detail: string;
  action: DataHealthAction | null;
};

export type DataHealthSourceState = {
  source: DataHealthSource;
  /** Последний день с данными. */
  lastDay: string | null;
  /** С какого дня вообще есть история. */
  firstDay: string | null;
  /** Строк за последний день — для подсказки. */
  lastCount: number | null;
  /** Сколько дней с данными в окне подробностей. */
  daysWithData: number;
  /** Дни окна подробностей: строк за каждый день, начиная с `from`. Пусто в кратком ответе. */
  days: number[];
};

export type DataHealthJob = {
  id: number;
  status: string;
  startedAt: number;
  completedAt: number | null;
  durationMs: number | null;
  triggeredBy: string;
  done: number;
  warn: number;
  error: number;
};

export type DataHealthStore = {
  storeId: string;
  storeName: string;
  /** Самая ранняя из дат источников: до неё полно всё. */
  coverageFloor: string | null;
  sources: DataHealthSourceState[];
  /** Дни без снимка остатков в окне значка. */
  gapDays: string[];
  lastJob: DataHealthJob | null;
  /** Шаги последнего обновления, которые не прошли. */
  failedSteps: Array<{ stepId: string; label: string; status: string; error: string | null }>;
  keysInvalid: boolean;
  /** Последние обновления — только в подробном ответе. */
  history: DataHealthJob[];
};

export type DataHealthPayload = {
  checkedAt: number;
  /** Первый день «ковра дней» подробного ответа. */
  from: string;
  /** Сегодня по МСК. */
  today: string;
  stores: DataHealthStore[];
  issues: DataHealthIssue[];
};

/** Самая тяжёлая из проблем — по ней горит значок. */
export function dataHealthSeverity(issues: readonly DataHealthIssue[]): DataHealthSeverity | null {
  if (issues.some(issue => issue.severity === "error")) return "error";
  return issues.length > 0 ? "warn" : null;
}

const SEVERITY_ORDER: Record<DataHealthSeverity, number> = { error: 0, warn: 1 };
const KIND_ORDER: Record<DataHealthIssueKind, number> = {
  auth: 0,
  refresh_late: 1,
  refresh_failed: 2,
  stale: 3,
  gap: 4,
  external: 5,
};

export function compareDataHealthIssues(a: DataHealthIssue, b: DataHealthIssue): number {
  const bySeverity = SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity];
  if (bySeverity !== 0) return bySeverity;
  const byKind = KIND_ORDER[a.kind] - KIND_ORDER[b.kind];
  if (byKind !== 0) return byKind;
  return a.title.localeCompare(b.title, "ru");
}

const DAY_FORMS = ["день", "дня", "дней"] as const;

export function dataHealthDays(count: number): string {
  const mod10 = count % 10;
  const mod100 = count % 100;
  const form = mod10 === 1 && mod100 !== 11 ? DAY_FORMS[0] : mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20) ? DAY_FORMS[1] : DAY_FORMS[2];
  return `${count} ${form}`;
}

const MONTHS_GENITIVE = ["января", "февраля", "марта", "апреля", "мая", "июня", "июля", "августа", "сентября", "октября", "ноября", "декабря"];

/** «14 августа»; с годом, если он не совпадает с сегодняшним. */
export function dataHealthDayLabel(day: string, today?: string): string {
  const [year, month, date] = day.split("-").map(Number);
  if (!year || !month || !date) return day;
  const sameYear = today ? today.slice(0, 4) === day.slice(0, 4) : true;
  return `${date} ${MONTHS_GENITIVE[month - 1]}${sameYear ? "" : ` ${year}`}`;
}

/** «12–14 августа», «12 августа и ещё 3 дня» — перечисление дыр без простыни дат. */
export function dataHealthDaysLabel(days: readonly string[], today?: string): string {
  if (days.length === 0) return "";
  if (days.length === 1) return dataHealthDayLabel(days[0]!, today);
  const first = dataHealthDayLabel(days[0]!, today);
  if (days.length <= 3) return `${days.slice(0, -1).map(day => dataHealthDayLabel(day, today)).join(", ")} и ${dataHealthDayLabel(days[days.length - 1]!, today)}`;
  return `${first} и ещё ${dataHealthDays(days.length - 1)}`;
}
